import { requireSupabase } from '@/lib/supabase';

export interface Conversation {
  id: string;
  userId: string;
  customerName: string;
  customerEmail: string;
  customerAvatar: string;
  subject: string;
  status: 'open' | 'closed';
  archived: boolean;
  unreadCustomer: number;
  unreadAdmin: number;
  isNewCustomer: boolean;
  lastActiveAt: string;
  isOnline: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ChatMessage {
  id: string;
  conversationId: string;
  senderId: string;
  senderRole: 'customer' | 'admin';
  message: string;
  seen: boolean;
  createdAt: string;
}

type ConvRow = {
  id: string;
  user_id: string;
  subject: string;
  status: string;
  archived: boolean;
  unread_customer: number;
  unread_admin: number;
  created_at: string;
  updated_at: string;
  profiles?: { name?: string | null; email?: string | null; avatar_url?: string | null } | null;
};

type MsgRow = {
  id: string;
  conversation_id: string;
  sender_id: string;
  sender_role: string;
  message: string;
  seen: boolean;
  created_at: string;
};

const CONV_SELECT = '*, profiles!user_id(name, email, avatar_url)';

function mapConv(row: ConvRow): Conversation {
  const fiveMinAgo = new Date(Date.now() - 5 * 60 * 1000);
  const lastActive = row.updated_at ? new Date(row.updated_at) : new Date(row.created_at);
  return {
    id: row.id,
    userId: row.user_id,
    customerName: row.profiles?.name || row.user_id.substring(0, 8),
    customerEmail: row.profiles?.email || '',
    customerAvatar: row.profiles?.avatar_url || '',
    subject: row.subject,
    status: row.status as 'open' | 'closed',
    archived: row.archived ?? false,
    unreadCustomer: row.unread_customer,
    unreadAdmin: row.unread_admin,
    isNewCustomer: false,
    lastActiveAt: lastActive.toISOString(),
    isOnline: lastActive > fiveMinAgo,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function mapMsg(row: MsgRow): ChatMessage {
  return {
    id: row.id,
    conversationId: row.conversation_id,
    senderId: row.sender_id,
    senderRole: row.sender_role as 'customer' | 'admin',
    message: row.message,
    seen: row.seen,
    createdAt: row.created_at,
  };
}

export async function getMyConversations(): Promise<Conversation[]> {
  const supabase = requireSupabase();
  const { data, error } = await supabase
    .from('support_conversations')
    .select(CONV_SELECT)
    .order('updated_at', { ascending: false });
  if (error) throw error;
  return ((data as ConvRow[]) ?? []).map(mapConv);
}

export async function getAdminConversations(includeArchived = false): Promise<Conversation[]> {
  const supabase = requireSupabase();
  let query = supabase
    .from('support_conversations')
    .select(CONV_SELECT)
    .order('updated_at', { ascending: false });
  if (!includeArchived) query = query.eq('archived', false);
  const { data, error } = await query;
  if (error) throw error;
  const convs = ((data as ConvRow[]) ?? []).map(mapConv);
  // Mark new customers (only 1 conversation)
  const counts: Record<string, number> = {};
  convs.forEach((c) => { counts[c.userId] = (counts[c.userId] || 0) + 1; });
  return convs.map((c) => ({ ...c, isNewCustomer: counts[c.userId] === 1 }));
}

export async function createConversation(subject: string): Promise<Conversation> {
  const supabase = requireSupabase();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated.');
  const { data, error } = await supabase
    .from('support_conversations')
    .insert({ user_id: user.id, subject })
    .select(CONV_SELECT)
    .single();
  if (error) throw error;
  return mapConv(data as ConvRow);
}

export async function getMessages(conversationId: string, viewAs?: 'customer' | 'admin'): Promise<ChatMessage[]> {
  const supabase = requireSupabase();
  const { data, error } = await supabase
    .from('support_messages')
    .select('*')
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return ((data as MsgRow[]) ?? []).map(mapMsg);
}

export async function openAndReadMessages(conversationId: string, viewerRole: 'customer' | 'admin'): Promise<ChatMessage[]> {
  const supabase = requireSupabase();
  // Mark as seen first
  await supabase.rpc('mark_messages_seen', { p_conversation_id: conversationId, p_reader_role: viewerRole });
  // Then fetch with updated seen status
  const { data, error } = await supabase
    .from('support_messages')
    .select('*')
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return ((data as MsgRow[]) ?? []).map(mapMsg);
}

export async function sendMessage(
  conversationId: string, message: string, senderRole: 'customer' | 'admin',
): Promise<ChatMessage> {
  const supabase = requireSupabase();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated.');

  const { data: conv } = await supabase.from('support_conversations').select('status, user_id').eq('id', conversationId).single();
  if (conv?.status === 'closed') throw new Error('This conversation is closed.');

  const { data, error } = await supabase
    .from('support_messages')
    .insert({ conversation_id: conversationId, sender_id: user.id, sender_role: senderRole, message })
    .select('*')
    .single();
  if (error) throw error;
  const counterField = senderRole === 'customer' ? 'unread_admin' : 'unread_customer';
  await supabase.rpc('increment_chat_unread', { p_conv_id: conversationId, p_field: counterField });

  // Email notification is NOT sent here immediately.
  // It is sent via a delayed check when the recipient opens the support page
  // (see checkUnreadConversations function).

  return mapMsg(data as MsgRow);
}

export async function markConversationRead(conversationId: string, role: 'customer' | 'admin'): Promise<void> {
  const supabase = requireSupabase();
  const field = role === 'customer' ? 'unread_customer' : 'unread_admin';
  await supabase.from('support_conversations').update({ [field]: 0, updated_at: new Date().toISOString() }).eq('id', conversationId);
}

export async function archiveConversation(conversationId: string): Promise<void> {
  const supabase = requireSupabase();
  await supabase.from('support_conversations').update({ archived: true }).eq('id', conversationId);
}

export async function unarchiveConversation(conversationId: string): Promise<void> {
  const supabase = requireSupabase();
  await supabase.from('support_conversations').update({ archived: false }).eq('id', conversationId);
}

export async function closeConversation(conversationId: string): Promise<void> {
  const supabase = requireSupabase();
  await supabase.from('support_conversations').update({ status: 'closed', updated_at: new Date().toISOString() }).eq('id', conversationId);
}

function createChannel(name: string, table: string, cb: (p: any) => void, filter?: string) {
  const supabase = requireSupabase();
  const channel = supabase.channel(name);
  const cfg: any = { event: '*', schema: 'public', table };
  if (filter) cfg.filter = filter;
  channel.on('postgres_changes', cfg, cb).subscribe();
  return { unsubscribe: () => { try { supabase.removeChannel(channel); } catch {} } };
}

// ── Typing indicator ──────────────────────────────────────────────────────────

const typingChannels: Record<string, any> = {};

export function subscribeToTyping(conversationId: string, role: 'customer' | 'admin', onTyping: (who: string) => void) {
  const supabase = requireSupabase();
  const channel = supabase.channel(`typing-${conversationId}`);
  channel.on('broadcast', { event: 'typing' }, (payload: any) => {
    if (payload.payload?.role !== role) onTyping(payload.payload?.who || 'Someone');
  }).on('broadcast', { event: 'stop_typing' }, (payload: any) => {
    if (payload.payload?.role !== role) onTyping('');
  }).subscribe();
  typingChannels[conversationId] = channel;
  return { unsubscribe: () => { try { supabase.removeChannel(channel); } catch {} } };
}

export function subscribeToAllTyping(role: 'customer' | 'admin', onTyping: (convId: string, who: string) => void) {
  const supabase = requireSupabase();
  const channel = supabase.channel('all-typing');
  channel.on('broadcast', { event: 'typing' }, (payload: any) => {
    if (payload.payload?.role !== role && payload.payload?.convId) {
      onTyping(payload.payload.convId, payload.payload?.who || 'Someone');
    }
  }).on('broadcast', { event: 'stop_typing' }, (payload: any) => {
    if (payload.payload?.role !== role && payload.payload?.convId) {
      onTyping(payload.payload.convId, '');
    }
  }).subscribe();
  return { unsubscribe: () => { try { supabase.removeChannel(channel); } catch {} } };
}

let typingTimer: any = null;

export function emitTyping(conversationId: string, role: 'customer' | 'admin', who: string) {
  const supabase = requireSupabase();
  const payload = { convId: conversationId, role, who };
  // Emit on both conv-specific and global channels
  let ch = typingChannels[conversationId];
  if (!ch) {
    ch = supabase.channel(`typing-${conversationId}`);
    ch.subscribe();
    typingChannels[conversationId] = ch;
  }
  ch.send({ type: 'broadcast', event: 'typing', payload });
  // Also emit on global channel
  const globalCh = supabase.channel('all-typing');
  globalCh.subscribe();
  globalCh.send({ type: 'broadcast', event: 'typing', payload });
  clearTimeout(typingTimer);
  typingTimer = setTimeout(() => {
    ch.send({ type: 'broadcast', event: 'stop_typing', payload });
    globalCh.send({ type: 'broadcast', event: 'stop_typing', payload });
  }, 2000);
}

// ── Delayed email notification ──────────────────────────────────────────────

export async function checkUnreadConversations(viewerRole: 'customer' | 'admin') {
  try {
    const supabase = requireSupabase();
    const token = (await supabase.auth.getSession()).data.session?.access_token;
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    if (!token || !supabaseUrl) return;

    // Load support settings
    const { data: settingsData } = await supabase.from('site_settings').select('settings').eq('id', 'default').single();
    const support = (settingsData as any)?.settings?.support;
    const delayMin = support?.emailDelayMinutes || 5;
    const resendKey = support?.resendApiKey;
    if (!resendKey) return;

    // Get conversations with unread messages for this role
    const unreadField = viewerRole === 'admin' ? 'unread_admin' : 'unread_customer';
    const { data: convs } = await supabase
      .from('support_conversations')
      .select('id, user_id, subject, updated_at, unread_admin, unread_customer, profiles!user_id(email, name)')
      .gt(unreadField, 0);

    if (!convs || convs.length === 0) return;

    const cutoff = new Date(Date.now() - delayMin * 60 * 1000);

    for (const conv of convs as any[]) {
      const lastActive = new Date(conv.updated_at);
      if (lastActive > cutoff) continue; // Still within delay window, skip

      // Check if we already sent a notification recently (within 1 hour)
      const notifKey = `np_notified_${conv.id}`;
      if (localStorage.getItem(notifKey) && Date.now() - Number(localStorage.getItem(notifKey)) < 3600000) continue;

      // Fetch unseen messages
      const senderRole = viewerRole === 'admin' ? 'customer' : 'admin';
      const { data: msgs } = await supabase
        .from('support_messages').select('message, created_at')
        .eq('conversation_id', conv.id).eq('sender_role', senderRole)
        .order('created_at', { ascending: false });

      if (!msgs || msgs.length === 0) continue;

      const recipientEmail = viewerRole === 'admin' ? 'admin@motionsdio.com' : conv.profiles?.email;
      const senderDisplayName = viewerRole === 'admin' ? (conv.profiles?.name || 'A customer') : 'Support Team';

      if (recipientEmail) {
        fetch(`${supabaseUrl}/functions/v1/notify-new-conversation`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            conversationId: conv.id, senderRole, senderDisplayName, recipientEmail,
            unseenMessages: (msgs || []).slice(0, 5).map((m: any) => m.message).reverse(),
            subject: conv.subject || 'Support',
          }),
        }).catch(() => {});
        localStorage.setItem(notifKey, String(Date.now()));
      }
    }
  } catch {}
}

export function emitStopTyping(conversationId: string, role: 'customer' | 'admin', who: string) {
  const payload = { convId: conversationId, role, who };
  const ch = typingChannels[conversationId];
  if (ch) ch.send({ type: 'broadcast', event: 'stop_typing', payload });
  try { requireSupabase().channel('all-typing').send({ type: 'broadcast', event: 'stop_typing', payload }); } catch {}
  clearTimeout(typingTimer);
}

// ── File upload ───────────────────────────────────────────────────────────────

export async function uploadChatFile(file: File): Promise<string> {
  const supabase = requireSupabase();
  const ext = file.name.split('.').pop()?.toLowerCase() || 'bin';
  const path = `${Date.now()}-${Math.random().toString(36).substring(2)}.${ext}`;
  const { error } = await supabase.storage.from('chat-files').upload(path, file, {
    contentType: file.type || undefined,
    upsert: false,
  });
  if (error) throw error;
  const { data: pub } = supabase.storage.from('chat-files').getPublicUrl(path);
  return pub.publicUrl;
}

export function subscribeToConversations(userId: string, callback: (payload: any) => void) {
  return createChannel('my-conv', 'support_conversations', callback, `user_id=eq.${userId}`);
}

export function subscribeToAdminConversations(callback: (payload: any) => void) {
  return createChannel('admin-conv', 'support_conversations', callback);
}

export function subscribeToMessages(conversationId: string, callback: (payload: any) => void) {
  return createChannel(`msg-${conversationId}`, 'support_messages', callback, `conversation_id=eq.${conversationId}`);
}
